import { measureLabel } from "./kpiCatalogUtils";

/** System calculation notes keyed by base KPI code (aligned with ETL / carrier_kpi bridge). */
const KPI_SYSTEM_CALCULATION = Object.freeze({
  on_time_pickup:
    "Aggregates pickup stops over each selection variant's rolling window (office-scoped when configured). " +
    "A stop counts as on-time when actual pickup arrival is on or before the scheduled pickup time plus the configured grace minutes. " +
    "Rate = success events ÷ total pickup events. The on-time rate is converted to a carrier_kpi penalty multiplier (1.0 = perfect; higher values penalize the carrier in resource selection scoring). " +
    "Refreshed nightly at 3:00 AM PST; use Calculate to run on demand.",
  on_time_delivery:
    "Aggregates delivery stops over the variant rolling window. " +
    "A stop counts as on-time when actual arrival is on or before the scheduled time (legacy backfill uses a 30-minute grace). " +
    "Rate = on-time deliveries ÷ total delivery events. Stored in carrier_kpi under the variant's carrier KPI name (often otd).",
  acceptance_rate:
    "Aggregates tender outcomes over each variant's rolling window. " +
    "Rate = accepted tenders ÷ (accepted + rejected + timed-out tenders) per carrier and office. " +
    "Converted to a carrier_kpi penalty multiplier for resource selection scoring.",
  claims_free_ratio:
    "Share of shipments without claims in the rolling window. " +
    "Stored per carrier in carrier_kpi for use in scoring when configured.",
});

/**
 * Hover / tooltip copy describing how this base KPI is calculated in the system.
 * @param {{ kpiCode?: string, description?: string, measures?: string[], selectionVariants?: object[] }} base
 */
export function buildKpiCalculationTooltip(base) {
  if (!base) return "KPI calculation details are not available.";

  const code = String(base.kpiCode || "").trim();
  const parts = [];

  const systemText = KPI_SYSTEM_CALCULATION[code];
  if (systemText) {
    parts.push(systemText);
  } else if (base.description?.trim()) {
    parts.push(base.description.trim());
  } else {
    parts.push(
      "Carrier-level values are computed per selection variant over each variant's rolling window and written to carrier_kpi for resource selection scoring."
    );
  }

  const variants = base.selectionVariants || [];
  if (variants.length) {
    const variantSummary = variants
      .map((variant) => {
        const days = variant.fixedDimensions?.rollingWindowDays;
        const scope = variant.fixedDimensions?.officeScoped ? "per office" : "global";
        const windowLabel = days ? `${days}-day window` : "rolling window";
        return `${variant.displayName || variant.variantCode} (${windowLabel}, ${scope}, carrier_kpi: ${variant.carrierKpiName})`;
      })
      .join("; ");
    parts.push(`Selection variants: ${variantSummary}.`);
  }

  const measures = (base.measures || []).map(measureLabel).filter(Boolean);
  if (measures.length) {
    parts.push(`Catalog measures: ${measures.join(", ")}.`);
  }

  return parts.join("\n\n");
}
